import type {
  Ability,
  Comprehension,
  DayOffset,
  SessionHour,
  SessionPreferences,
  SessionWindow,
  TideState,
  WaterComfort,
  WindowLabel,
  ComfortVerdict,
} from "./types";
import { comfortVerdict, type SessionContext } from "./comfort";
import {
  personalFitContext,
  prefAbility,
  prefScoringKey,
  type EquipmentRange,
  type FoilBoardSetup,
  type FoilSetup,
} from "./preferences";
import {
  waveHeightDescriptor,
  waveHeightFt,
  wetsuitRecommendation,
  windQualityLabel,
} from "../conditions/surf-labels";
import { degToCompass } from "../conditions/scoring";
import { assessSwimChop } from "../conditions/swim-analysis";

const KT = 1.852;
const kt = (kmh: number) => kmh / KT;

function avg(xs: (number | null)[]): number | null {
  const v = xs.filter((x): x is number => x !== null);
  if (!v.length) return null;
  return v.reduce((s, x) => s + x, 0) / v.length;
}

function cap(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}

type Repr = {
  waveHeightM: number;
  windWaveHeightM: number | null;
  period: number | null;
  windKmh: number;
  windDir: number | null;
  swellDir: number | null;
  gustKmh: number | null;
  tideState: TideState | null;
  tidePosition: number | null;
  waterTempC: number | null;
  atmosphericVisibilityM: number | null;
  underwaterVisibilityM: number | null;
  underwaterVisibilitySource: "secchi" | "chlorophyll-inferred" | "unavailable";
  currentVelocityMs: number | null;
};

function repr(window: SessionWindow): Repr {
  // Describe the PEAK hours (the time we headline), not the full acceptable band,
  // so the score/conditions stay stable when a timing filter only changes the
  // band's extent. Falls back to all hours if the peak slice is empty.
  const peakHs = window.hours.filter(
    (h) => h.hour >= window.peakStartHour && h.hour < window.peakEndHour
  );
  const hs = peakHs.length ? peakHs : window.hours;
  const mid = hs[Math.floor(hs.length / 2)];
  const hasFullChop = hs.every(
    (hour) => hour.windWaveHeightM !== null && hour.windWaveHeightM !== undefined,
  );
  return {
    waveHeightM: avg(hs.map((h) => h.waveHeightM)) ?? 0,
    windWaveHeightM: hasFullChop
      ? avg(hs.map((h) => h.windWaveHeightM ?? null))
      : null,
    period: avg(hs.map((h) => h.swellPeriodS)),
    windKmh: avg(hs.map((h) => h.windSpeedKmh)) ?? 0,
    windDir: mid.windDirDeg,
    swellDir: mid.swellDirDeg,
    gustKmh: avg(hs.map((h) => h.windGustKmh)),
    tideState: mid.tideState,
    tidePosition: mid.tidePosition,
    waterTempC: hs[0].waterTempC,
    atmosphericVisibilityM: avg(hs.map((h) => h.visibilityM)),
    underwaterVisibilityM: avg(hs.map((h) => h.underwaterVisibilityM ?? null)),
    underwaterVisibilitySource: hs.find((h) => h.underwaterVisibilitySource)?.underwaterVisibilitySource ?? "unavailable",
    currentVelocityMs: avg(hs.map((h) => h.currentVelocityMs ?? null)),
  };
}

function representativeHour(window: SessionWindow): SessionHour {
  const peakHours = window.hours.filter(
    (h) => h.hour >= window.peakStartHour && h.hour < window.peakEndHour
  );
  const hours = peakHours.length ? peakHours : window.hours;
  return hours[Math.floor(hours.length / 2)];
}

function windWord(kmh: number): string {
  const k = kt(kmh);
  if (k < 5) return "calm";
  if (k < 9) return "a light wind";
  if (k < 14) return "a moderate wind";
  if (k < 20) return "a fresh wind";
  if (k < 27) return "a strong wind";
  return "a very strong wind";
}

function tidePhrase(state: TideState | null, position: number | null): string {
  if (!state) return "";
  if (state === "high") return "high tide";
  if (state === "low") return "low tide";
  const pos = position === null ? "" : position < 0.4 ? "low " : position < 0.7 ? "mid " : "high ";
  return `${state === "rising" ? "incoming" : "dropping"} ${pos}tide`.replace(/\s+/g, " ").trim();
}

function windQualityWord(r: Repr, ctx: SessionContext): string | null {
  if (ctx.beachFacingDeg === null || ctx.beachFacingDeg === undefined || r.windDir === null) {
    return null;
  }
  return windQualityLabel(r.windDir, ctx.beachFacingDeg).label.toLowerCase();
}

// --- headlines -------------------------------------------------------------

function waveHeadline(r: Repr, comp: Comprehension, ctx: SessionContext): string {
  if (comp === "technical") {
    let swell = waveHeightFt(r.waveHeightM);
    if (r.period !== null) {
      swell += ` at ${Math.round(r.period)}s`;
      if (r.swellDir !== null) swell += ` ${degToCompass(r.swellDir)}`;
    }
    let wind = `${Math.round(kt(r.windKmh))}kt`;
    if (r.windDir !== null) wind += ` ${degToCompass(r.windDir)}`;
    const tide = tidePhrase(r.tideState, r.tidePosition);
    return [swell, wind, tide].filter(Boolean).join(", ");
  }
  const quality = windQualityWord(r, ctx);
  const windPart = quality ? `${quality} wind` : windWord(r.windKmh);
  const tide = tidePhrase(r.tideState, r.tidePosition);
  return cap([waveHeightDescriptor(r.waveHeightM), windPart, tide].filter(Boolean).join(", "));
}

function windHeadline(r: Repr, comp: Comprehension): string {
  if (comp === "technical") {
    let wind = `${Math.round(kt(r.windKmh))}kt`;
    if (r.windDir !== null) wind += ` ${degToCompass(r.windDir)}`;
    const gust = r.gustKmh !== null ? `gusting ${Math.round(kt(r.gustKmh))}kt` : "";
    return [wind, gust].filter(Boolean).join(", ");
  }
  const dir = r.windDir !== null ? ` from the ${degToCompass(r.windDir)}` : "";
  return cap(`${windWord(r.windKmh)}${dir}`);
}

function swimHeadline(r: Repr, comp: Comprehension): string {
  const temp = r.waterTempC !== null ? `Water ${Math.round(r.waterTempC)}C` : "Water temp unknown";
  const chop = assessSwimChop({
    swellHeightM: r.waveHeightM,
    windWaveHeightM: r.windWaveHeightM,
    swellPeriodS: r.period,
    windSpeedKmh: r.windKmh,
  });
  if (comp === "technical") {
    return chop.confidence === "full"
      ? `${temp}, ${Math.round(kt(r.windKmh))}kt wind, ${chop.effectiveChopM.toFixed(1)}m measured chop`
      : `${temp}, ${Math.round(kt(r.windKmh))}kt wind, chop inferred from wind (measurement unavailable)`;
  }
  if (chop.confidence === "inferred") {
    return `${temp}, chop inferred from wind (measurement unavailable)`;
  }
  return `${temp}, ${chop.effectiveChopM > 0.25 ? "some measured chop" : "measured calm water"}`;
}

function supHeadline(prefs: SessionPreferences, r: Repr, comp: Comprehension, ctx: SessionContext): string {
  const scoringKey = prefScoringKey(prefs);
  if (scoringKey === "sup-surf") return waveHeadline(r, comp, ctx);
  if (scoringKey === "sup-downwind") return windHeadline(r, comp);
  const style = prefs.values.style ?? "touring";
  if (style === "downwind") return windHeadline(r, comp);
  if (style === "surf") return waveHeadline(r, comp, ctx);
  // touring / fitness
  if (comp === "technical") {
    return `${Math.round(kt(r.windKmh))}kt wind, ${r.waveHeightM.toFixed(1)}m`;
  }
  const flat = r.windKmh < 12 && r.waveHeightM < 0.3 ? "glassy and flat" : "some texture";
  return cap(`${windWord(r.windKmh)}, ${flat}`);
}

function divingHeadline(r: Repr): string {
  const underwater = r.underwaterVisibilityM !== null
    ? `Underwater visibility ${Number(r.underwaterVisibilityM.toFixed(1))}m`
    : r.underwaterVisibilitySource === "chlorophyll-inferred"
      ? "Underwater visibility inferred from chlorophyll"
      : "Underwater visibility unavailable";
  const atmospheric = r.atmosphericVisibilityM !== null
    ? `Atmospheric visibility ${Number((r.atmosphericVisibilityM / 1000).toFixed(1))}km`
    : "Atmospheric visibility unavailable";
  const current = r.currentVelocityMs !== null
    ? `Current ${Number(r.currentVelocityMs.toFixed(2))}m/s, direction unavailable`
    : "Current unavailable, direction unavailable";
  return `${underwater}, ${atmospheric}, ${current}`;
}

function headlineFor(
  prefs: SessionPreferences,
  r: Repr,
  comp: Comprehension,
  ctx: SessionContext
): string {
  switch (prefs.sport) {
    case "surf":
    case "bodyboarding":
      return waveHeadline(r, comp, ctx);
    case "wind":
    case "sailing":
      return windHeadline(r, comp);
    case "foil": {
      const key = prefScoringKey(prefs);
      return key === "foil" ? waveHeadline(r, comp, ctx) : windHeadline(r, comp);
    }
    case "sup":
      return supHeadline(prefs, r, comp, ctx);
    case "swim":
      return swimHeadline(r, comp);
    case "diving":
      return divingHeadline(r);
    default:
      return windHeadline(r, comp);
  }
}

// --- verdicts --------------------------------------------------------------

const ABILITY_WORD: Record<Ability, string> = {
  beginner: "beginner",
  intermediate: "intermediate",
  advanced: "advanced",
  expert: "expert",
};

const EQUIPMENT_WORD: Record<EquipmentRange, string> = {
  unknown: "unspecified equipment range",
  "light-wind": "light-wind equipment range",
  "all-round": "all-round equipment range",
  "high-wind": "high-wind equipment range",
};

const BOARD_WORD: Record<FoilBoardSetup, string> = {
  unknown: "unspecified board",
  stable: "stable board",
  performance: "performance board",
};

const FOIL_WORD: Record<FoilSetup, string> = {
  unknown: "unspecified foil",
  "low-speed": "low-speed foil",
  "all-round": "all-round foil",
  "high-speed": "high-speed foil",
};

function equipmentPhrase(range: EquipmentRange): string {
  const article = range === "unknown" || range === "all-round" ? "an" : "a";
  return `${article} ${EQUIPMENT_WORD[range]}`;
}

function withIndefiniteArticle(value: string): string {
  return /^[aeiou]/i.test(value) ? `an ${value}` : `a ${value}`;
}

function personalProfileDescription(prefs: SessionPreferences): string | null {
  const profile = personalFitContext(prefs);
  if (!profile) return null;

  if (profile.kind === "bodyboarding") {
    if (profile.wavePreference === "unknown") return null;
    return `${profile.ability} ${profile.wavePreference}-wave Bodyboarding profile`;
  }
  if (profile.kind === "wind") {
    if (profile.equipmentRange === "unknown") return null;
    return `${profile.ability} ${profile.mode} profile with ${equipmentPhrase(profile.equipmentRange)}`;
  }
  if (profile.kind === "diving") {
    return `${profile.ability} ${profile.mode} Diving context with ${profile.training} training selected, ${profile.plannedDepth} planned depth and ${profile.entry} entry`;
  }
  if (profile.kind === "sup") {
    if (profile.profile === "sup-touring") {
      return `${profile.ability} Touring SUP profile with a ${profile.boardType} board`;
    }
    if (profile.profile === "sup-surf") {
      return `${profile.surfCompetence} SUP Surf profile with a ${profile.boardType} board`;
    }
    return `${profile.downwindCompetence} Downwind SUP profile with a ${profile.boardType} board`;
  }

  if (
    profile.boardSetup === "unknown" &&
    profile.foilSetup === "unknown" &&
    (profile.mode !== "prone" || profile.pumpingAbility === "unknown") &&
    (profile.mode !== "wing" || profile.wingRange === "unknown")
  ) {
    return null;
  }

  const pumping = profile.mode === "prone" && profile.pumpingAbility !== "unknown"
    ? `, ${profile.pumpingAbility} pumping`
    : "";
  const board = withIndefiniteArticle(BOARD_WORD[profile.boardSetup]);
  const foil = withIndefiniteArticle(FOIL_WORD[profile.foilSetup]);
  const base = `${profile.ability} ${profile.mode} profile with ${board} and ${foil}${pumping}`;
  if (profile.mode !== "wing") return base;
  const wingRange = withIndefiniteArticle(
    EQUIPMENT_WORD[profile.wingRange].replace("equipment", "wing")
  );
  return `${profile.ability} wing profile with ${board}, ${foil} and ${wingRange}`;
}

function personalProfileReason(
  label: WindowLabel,
  prefs: SessionPreferences,
  verdict: ComfortVerdict
): string {
  const description = personalProfileDescription(prefs);
  if (!description) return "";

  if (label === "too-weak") {
    return `${verdict.excludedBy ?? "Conditions"} is the limiting personal constraint for your ${description}.`;
  }

  if (label === "too-strong") {
    return `${verdict.excludedBy ?? "Conditions"} exceeds the personal control range for your ${description}.`;
  }

  const profile = personalFitContext(prefs);
  if (profile?.kind === "bodyboarding" && profile.wavePreference === "shorebreak") {
    return `This matches your preference, but local shorebreak context is unprofiled.`;
  }
  if (profile?.kind === "wind" && profile.equipmentRange !== "unknown") {
    const equipment = profile.equipmentRange === "all-round"
      ? "all-round kit"
      : `${profile.equipmentRange} kit`;
    const mode = profile.mode === "windsurf" ? "Windsurfing" : "Kitesurfing";
    const equipmentEffect = profile.equipmentRange === "all-round"
      ? "Your all-round kit follows your preferred-wind fit."
      : `Your ${equipment} affected this personal ranking.`;
    return `This fits your ${description}. ${equipmentEffect} ${mode} is context only and did not change the numeric fit.`;
  }
  if (profile?.kind === "diving") {
    const selected = profile.mode === "low-visibility"
      ? "selected low-visibility context"
      : profile.mode === "drift"
        ? "selected drift context"
        : "selected recreational context";
    return `This matches your ${selected}. Measured visibility and current affect personal fit, but local entry and depth context is unavailable without reviewed geometry and bathymetry. This does not confirm competence or safety clearance.`;
  }
  return `This fits your ${description}.`;
}

/** Explicit copy for the independent canonical red-safety veto. */
export function redSafetyExclusionReason(
  ctx: SessionContext,
  hours: readonly SessionHour[] = [],
): string | null {
  const redHours = hours.filter((hour) => hour.safetyState === "flagged-red");
  const hasTodayFallback = hours.some((hour) =>
    hour.dayOffset === 0 &&
    (hour.safetyState === undefined || hour.safetyState === "no-data")
  );
  if (redHours.length === 0 && !(ctx.safetyState === "flagged-red" && hasTodayFallback)) {
    return null;
  }
  const note = redHours.flatMap((hour) => hour.safetyNotes ?? [])
    .find((value) => value.trim())?.trim()
    ?? ctx.safetyNotes?.find((value) => value.trim())?.trim();
  return note
    ? `No personal window is shown because the canonical safety assessment is red: ${note}.`
    : "No personal window is shown because the canonical safety assessment is red.";
}

function isWaveSport(prefs: SessionPreferences): boolean {
  if (prefs.sport === "surf" || prefs.sport === "bodyboarding") return true;
  if (prefs.sport === "sup" && prefScoringKey(prefs) === "sup-surf") return true;
  if (prefs.sport === "foil" && prefScoringKey(prefs) === "foil") return true;
  return false;
}

function tooStrongPhrase(
  prefs: SessionPreferences,
  ability: Ability,
  excludedBy: string | null
): string {
  if (isWaveSport(prefs)) {
    return `Too big for a ${ABILITY_WORD[ability]} session here. Best left to the experienced today.`;
  }
  if (prefs.sport === "sailing") {
    return `Too much wind for a ${prefs.values.boat ?? "dinghy"} at this level. One to sit out.`;
  }
  if (excludedBy?.includes("wind")) {
    return `The wind control range is exceeded for a ${ABILITY_WORD[ability]} session.`;
  }
  if (excludedBy?.includes("swell") || excludedBy?.includes("sea-state")) {
    return `The swell control range is exceeded for a ${ABILITY_WORD[ability]} session.`;
  }
  return `These conditions exceed the control range for a ${ABILITY_WORD[ability]} session.`;
}

function tooWeakPhrase(prefs: SessionPreferences, excludedBy: string | null): string {
  switch (excludedBy) {
    case "bodyboarding punch":
      return "Not enough hollow-wave punch for this profile.";
    case "prone pumpable energy":
      return "The swell does not carry enough pumpable energy.";
    case "prone pumping progression":
      return "This marginal swell needs stronger pumping progression or low-speed lift.";
    case "wind and swell alignment":
      return "The wind and swell alignment does not form a clean downwind bump train.";
    case "downwind combined bump energy":
      return "The combined swell and wind-wave energy is too weak for a downwind run.";
    case "wing equipment wind range":
      return "The wind is below the selected wing equipment range.";
    case "swell":
      return "The swell is below this profile's usable range.";
  }
  if (isWaveSport(prefs)) {
    return "Too small to be worth the paddle out.";
  }
  if (prefs.sport === "swim") {
    return "Fine for a gentle swim, but nothing to write home about.";
  }
  return "Not enough wind to get going.";
}

function verdictPhrase(
  label: WindowLabel,
  prefs: SessionPreferences,
  ability: Ability,
  excludedBy: string | null
): string {
  const profile = personalFitContext(prefs);
  if (profile?.kind === "sup") {
    switch (label) {
      case "best":
        return "Best match for your selected profile.";
      case "good":
        return "A good match for your selected profile.";
      case "possible":
        return "A possible match for your selected profile.";
      case "too-strong":
        return "These conditions exceed the selected profile's declared limits.";
      case "too-weak":
        return "These conditions fall below the selected profile's usable range.";
      case "not-recommended":
        return "Conditions here are poor for this selected profile.";
    }
  }
  switch (label) {
    case "best":
      return ability === "beginner" || ability === "intermediate"
        ? "Spot on for your level."
        : "Prime conditions for the session.";
    case "good":
      return "A good session on offer.";
    case "possible":
      return "Worth getting out, just a notch off the best.";
    case "not-recommended":
      return "Conditions here are poor, not one for this window.";
    case "too-strong":
      return tooStrongPhrase(prefs, ability, excludedBy);
    case "too-weak":
      return tooWeakPhrase(prefs, excludedBy);
    default:
      return "";
  }
}

export function describeWindow(
  window: SessionWindow,
  prefs: SessionPreferences,
  comprehension: Comprehension,
  ctx: SessionContext
): { headline: string; explanation: string } {
  const r = repr(window);
  const ability = prefAbility(prefs);
  const headline = headlineFor(prefs, r, comprehension, ctx);
  const fitVerdict = comfortVerdict(prefs, representativeHour(window), ctx);
  const verdict = verdictPhrase(window.label, prefs, ability, fitVerdict.excludedBy);
  const profileReason = personalProfileReason(window.label, prefs, fitVerdict);

  let lead = "";
  if (window.label === "best" || window.label === "good" || window.label === "possible") {
    lead = `${headline}. `;
  }
  const explanation = `${lead}${verdict}${profileReason ? ` ${profileReason}` : ""}`.trim();
  return { headline, explanation };
}

function reprFromHour(h: SessionHour): Repr {
  return {
    waveHeightM: h.waveHeightM ?? 0,
    windWaveHeightM: h.windWaveHeightM ?? null,
    period: h.swellPeriodS,
    windKmh: h.windSpeedKmh,
    windDir: h.windDirDeg,
    swellDir: h.swellDirDeg,
    gustKmh: h.windGustKmh,
    tideState: h.tideState,
    tidePosition: h.tidePosition,
    waterTempC: h.waterTempC,
    atmosphericVisibilityM: h.visibilityM,
    underwaterVisibilityM: h.underwaterVisibilityM ?? null,
    underwaterVisibilitySource: h.underwaterVisibilitySource ?? "unavailable",
    currentVelocityMs: h.currentVelocityMs ?? null,
  };
}

/** A compact, comprehension-aware metric line for a single hour (breakdown rows). */
export function hourSummary(
  prefs: SessionPreferences,
  hour: SessionHour,
  comprehension: Comprehension,
  ctx: SessionContext
): string {
  return headlineFor(prefs, reprFromHour(hour), comprehension, ctx);
}

// --- water comfort ---------------------------------------------------------

function tempLabel(c: number | null): string {
  if (c === null) return "Unknown";
  if (c < 6) return "Ice";
  if (c < 11) return "Cold";
  if (c < 16) return "Cool";
  if (c < 20) return "Mild";
  if (c < 24) return "Warm";
  return "Tropical";
}

function isComfortable(prefs: SessionPreferences, c: number | null): boolean {
  if (c === null) return true;
  if (prefs.sport === "swim") {
    switch (prefs.values.tempComfort) {
      case "warm":
        return c >= 18;
      case "moderate":
        return c >= 14;
      case "cold":
      default:
        return true;
    }
  }
  return true;
}

function swimSkinNote(c: number | null): string {
  if (c === null) return "Check locally";
  if (c >= 20) return "Comfortable for skins";
  if (c >= 16) return "Skins for short swims, wetsuit for longer";
  if (c >= 11) return "Wetsuit recommended";
  return "Wetsuit, gloves and a hat for cold-water swimming";
}

export function waterComfortFor(
  prefs: SessionPreferences,
  waterTempC: number | null
): WaterComfort {
  const advice = prefs.sport === "swim" ? swimSkinNote(waterTempC) : wetsuitRecommendation(waterTempC);
  return {
    waterTempC,
    label: tempLabel(waterTempC),
    advice,
    comfortable: isComfortable(prefs, waterTempC),
  };
}

// --- disclosure helpers ----------------------------------------------------

/** A coloured offshore/onshore chip from wind direction vs the beach bearing. */
export function windQualityChip(
  windDirDeg: number | null,
  beachFacingDeg: number | null
): { label: string; tone: "good" | "mixed" | "poor" } | null {
  if (windDirDeg === null || beachFacingDeg === null) {
    return null;
  }
  const { label, score } = windQualityLabel(windDirDeg, beachFacingDeg);
  const tone = score >= 75 ? "good" : score >= 40 ? "mixed" : "poor";
  return { label, tone };
}

/** First and last daylight hour of a day, from the per-hour isDay flag. */
export function daylightBand(
  hours: readonly Pick<SessionHour, "dayOffset" | "hour" | "isDay">[],
  dayOffset: DayOffset
): { firstLightHour: number; lastLightHour: number } | null {
  const dayHours = hours.filter((h) => h.dayOffset === dayOffset && h.isDay).map((h) => h.hour);
  if (!dayHours.length) return null;
  return { firstLightHour: Math.min(...dayHours), lastLightHour: Math.max(...dayHours) };
}
